import { supabase } from '@/integrations/supabase/client';
import {
  COUNTRY_PATTERNS,
  GOOGLE_HOLIDAY_CALENDARS,
  extractCountryFromAddress,
  getCountryName as getCountryNameFromConstants,
} from './holiday-constants';

// Re-export for backward compatibility
export { extractCountryFromAddress };

// Holiday types and interfaces
export interface Holiday {
  date: string; // Format: YYYY-MM-DD
  name: string;
  type: 'public' | 'observance' | 'bank' | 'regional';
  isNonWorkingDay: boolean;
  description?: string;
  source: 'google-calendar' | 'cache';
}

export interface CountryHolidays {
  country: string;
  countryCode: string;
  year: number;
  holidays: Holiday[];
}

// Google Calendar API response interfaces
interface GoogleCalendarEvent {
  id: string;
  summary: string;
  description?: string;
  start: {
    date?: string;
    dateTime?: string;
  };
  end: {
    date?: string;
    dateTime?: string;
  };
  transparency?: 'opaque' | 'transparent';
}

// In-memory cache for holiday data
const HOLIDAY_CACHE = new Map<string, { data: Holiday[], expiry: number }>();
const CACHE_DURATION = 24 * 60 * 60 * 1000; // 24 hours in milliseconds

// Google Calendar API service
class GoogleCalendarHolidayService {
  private apiKey: string | null = null;

  constructor() {
    this.apiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY;
  }

  private isApiAvailable(): boolean {
    return !!this.apiKey;
  }

  private getCacheKey(countryCode: string, year: number): string {
    return `holidays_${countryCode}_${year}`;
  }

  private getCachedData(countryCode: string, year: number): Holiday[] | null {
    const cacheKey = this.getCacheKey(countryCode, year);
    const cached = HOLIDAY_CACHE.get(cacheKey);
    
    if (cached && cached.expiry > Date.now()) {
      return cached.data.map(holiday => ({ ...holiday, source: 'cache' as const }));
    }
    
    // Remove expired cache entry
    if (cached) {
      HOLIDAY_CACHE.delete(cacheKey);
    }
    
    return null;
  }

  private setCachedData(countryCode: string, year: number, data: Holiday[]): void {
    const cacheKey = this.getCacheKey(countryCode, year);
    HOLIDAY_CACHE.set(cacheKey, {
      data,
      expiry: Date.now() + CACHE_DURATION
    });
  }

  private convertGoogleEventToHoliday(event: GoogleCalendarEvent): Holiday {
    let dateStr: string;
    
    if (event.start.date) {
      // All-day event - use the date directly (this should be YYYY-MM-DD format)
      dateStr = event.start.date;
    } else if (event.start.dateTime) {
      // Time-specific event - this shouldn't happen for holidays but handle it
      // Parse the dateTime and get date in UTC to avoid timezone shifts
      const eventDate = new Date(event.start.dateTime);
      const utcYear = eventDate.getUTCFullYear();
      const utcMonth = eventDate.getUTCMonth() + 1;
      const utcDate = eventDate.getUTCDate();
      dateStr = `${utcYear}-${String(utcMonth).padStart(2, '0')}-${String(utcDate).padStart(2, '0')}`;
    } else {
      throw new Error('Invalid event date');
    }

    // Determine if it's a non-working day based on the event title/description
    const isNonWorkingDay = this.isNonWorkingHoliday(event.summary, event.description);

    return {
      date: dateStr,
      name: event.summary,
      type: isNonWorkingDay ? 'public' : 'observance',
      isNonWorkingDay,
      description: event.description,
      source: 'google-calendar'
    };
  }

  private isNonWorkingHoliday(title: string, description?: string): boolean {
    const nonWorkingKeywords = [
      'national holiday', 'public holiday', 'bank holiday', 'federal holiday',
      'christmas', 'new year', 'independence', 'thanksgiving', 'easter',
      'labor day', 'memorial day', 'veterans day', 'martin luther king',
      'presidents day', 'good friday', 'boxing day', 'victoria day',
      'canada day', 'anzac day', 'queens birthday', 'kings birthday'
    ];
    
    const text = `${title} ${description || ''}`.toLowerCase();
    return nonWorkingKeywords.some(keyword => text.includes(keyword));
  }

  // Check Supabase for existing holidays
  private async getHolidaysFromSupabase(countryCode: string, year: number): Promise<Holiday[]> {
    try {
      const { data, error } = await supabase
        .from('holidays')
        .select('*')
        .eq('country_code', countryCode)
        .eq('year', year)
        .gte('updated_at', new Date(Date.now() - CACHE_DURATION).toISOString());

      if (error) {
        console.warn('Error fetching holidays from Supabase:', error);
        return [];
      }

      if (data && data.length > 0) {
        return data.map(row => ({
          date: row.date,
          name: row.name,
          type: row.type as 'public' | 'observance' | 'bank' | 'regional',
          isNonWorkingDay: row.is_non_working_day,
          description: row.description,
          source: 'cache' as const
        }));
      }

      return [];
    } catch (error) {
      console.warn('Error accessing Supabase holidays:', error);
      return [];
    }
  }

  async fetchHolidays(countryCode: string, year: number): Promise<Holiday[]> {
    // Check in-memory cache first
    const cached = this.getCachedData(countryCode, year);
    if (cached) {
      return cached;
    }

    // Check Supabase cache
    const supabaseHolidays = await this.getHolidaysFromSupabase(countryCode, year);
    if (supabaseHolidays.length > 0) {
      this.setCachedData(countryCode, year, supabaseHolidays);
      return supabaseHolidays;
    }

    // If API is not available, return empty array
    if (!this.isApiAvailable()) {
      console.warn('Google Maps API key not configured. Add VITE_GOOGLE_MAPS_API_KEY to enable holiday detection.');
      return [];
    }

    const calendarId = GOOGLE_HOLIDAY_CALENDARS[countryCode];
    if (!calendarId) {
      console.warn(`No Google Calendar holiday calendar found for country: ${countryCode}`);
      return [];
    }

    try {
      const startDate = `${year}-01-01`;
      const endDate = `${year}-12-31`;
      
      const url = new URL('https://www.googleapis.com/calendar/v3/calendars/' + encodeURIComponent(calendarId) + '/events');
      url.searchParams.set('key', this.apiKey!);
      url.searchParams.set('timeMin', startDate + 'T00:00:00Z');
      url.searchParams.set('timeMax', endDate + 'T23:59:59Z');
      url.searchParams.set('singleEvents', 'true');
      url.searchParams.set('orderBy', 'startTime');
      url.searchParams.set('maxResults', '50');

      const response = await fetch(url.toString());
      
      if (!response.ok) {
        if (response.status === 403) {
          console.error('Google Calendar API quota exceeded or access denied');
        } else if (response.status === 400) {
          console.error('Google Calendar API bad request - check calendar ID and API key');
        } else {
          console.error(`Google Calendar API error: ${response.status} ${response.statusText}`);
        }
        return [];
      }

      const data = await response.json();
      
      if (!data.items) {
        console.warn(`No holiday events found for ${countryCode} in ${year}`);
        return [];
      }

      const holidays = data.items.map((event: GoogleCalendarEvent) => 
        this.convertGoogleEventToHoliday(event)
      );

      // Cache in memory only: the shared holidays table is written by the populate-holidays edge function
      this.setCachedData(countryCode, year, holidays);
      
      console.log(`Fetched ${holidays.length} holidays for ${countryCode} ${year} from Google Calendar`);
      return holidays;
    } catch (error) {
      console.error('Error fetching holidays from Google Calendar API:', error);
      return [];
    }
  }

  async fetchHolidaysInRange(countryCode: string, startDate: Date, endDate: Date): Promise<Holiday[]> {
    const startYear = startDate.getFullYear();
    const endYear = endDate.getFullYear();
    
    // Fetch holidays for all years in the range
    const allHolidays: Holiday[] = [];
    for (let year = startYear; year <= endYear; year++) {
      const yearHolidays = await this.fetchHolidays(countryCode, year);
      allHolidays.push(...yearHolidays);
    }
    
    // Filter to the specific date range
    return allHolidays.filter(holiday => {
      const holidayDate = new Date(holiday.date);
      return holidayDate >= startDate && holidayDate <= endDate;
    });
  }

  // Clear cache for a specific country/year or all cache
  clearCache(countryCode?: string, year?: number): void {
    if (countryCode && year) {
      const cacheKey = this.getCacheKey(countryCode, year);
      HOLIDAY_CACHE.delete(cacheKey);
    } else {
      HOLIDAY_CACHE.clear();
    }
  }

  // Get cache stats
  getCacheStats(): { size: number, keys: string[] } {
    return {
      size: HOLIDAY_CACHE.size,
      keys: Array.from(HOLIDAY_CACHE.keys())
    };
  }
}

// Create a singleton instance
const googleCalendarService = new GoogleCalendarHolidayService();

// Clear all in-memory holiday caches (the shared holidays table is not client-writable)
export async function clearAllHolidayCache(): Promise<void> {
  googleCalendarService.clearCache();
  SYNC_HOLIDAY_CACHE.clear();
}

// Clear cache for specific country/year
export function clearHolidayCache(countryCode?: string, year?: number): void {
  googleCalendarService.clearCache(countryCode, year);
  if (countryCode && year) {
    const cacheKey = `${countryCode}_${year}`;
    SYNC_HOLIDAY_CACHE.delete(cacheKey);
  } else {
    SYNC_HOLIDAY_CACHE.clear();
  }
}

// Global cache for quick synchronous access
const SYNC_HOLIDAY_CACHE = new Map<string, Holiday[]>();

// Initialize cache and attempt to fetch from API
async function initializeHolidayCache(countryCode: string, year: number): Promise<void> {
  const cacheKey = `${countryCode}_${year}`;
  
  if (SYNC_HOLIDAY_CACHE.has(cacheKey)) {
    return; // Already initialized
  }

  // Set empty array initially to prevent multiple simultaneous requests
  SYNC_HOLIDAY_CACHE.set(cacheKey, []);

  try {
    const apiHolidays = await googleCalendarService.fetchHolidays(countryCode, year);
    SYNC_HOLIDAY_CACHE.set(cacheKey, apiHolidays);
  } catch (error) {
    console.warn(`Failed to fetch holidays from Google Calendar for ${countryCode} ${year}:`, error);
    // Keep empty array rather than removing from cache
  }
}

// Get holidays for a specific country and year (async version)
export async function getHolidaysForCountryAsync(countryCode: string, year: number): Promise<Holiday[]> {
  return await googleCalendarService.fetchHolidays(countryCode, year);
}

// Get holidays for a specific country and year (sync version - uses cache)
export function getHolidaysForCountry(countryCode: string, year: number): Holiday[] {
  const cacheKey = `${countryCode}_${year}`;
  const holidays = SYNC_HOLIDAY_CACHE.get(cacheKey);
  
  if (holidays === undefined) {
    // Initialize with empty array and trigger async fetch
    SYNC_HOLIDAY_CACHE.set(cacheKey, []);
    initializeHolidayCache(countryCode, year);
    return [];
  }
  
  return holidays;
}

// Get holidays for a date range (async version)
export async function getHolidaysInRangeAsync(countryCode: string, startDate: Date, endDate: Date): Promise<Holiday[]> {
  return await googleCalendarService.fetchHolidaysInRange(countryCode, startDate, endDate);
}

// Get holidays for a date range (sync version - uses cache)
export function getHolidaysInRange(countryCode: string, startDate: Date, endDate: Date): Holiday[] {
  const startYear = startDate.getFullYear();
  const endYear = endDate.getFullYear();
  
  const allHolidays: Holiday[] = [];
  
  // Get holidays for all years in the range
  for (let year = startYear; year <= endYear; year++) {
    const yearHolidays = getHolidaysForCountry(countryCode, year);
    allHolidays.push(...yearHolidays);
  }
  
  // Filter to the specific date range
  return allHolidays.filter(holiday => {
    const holidayDate = new Date(holiday.date);
    return holidayDate >= startDate && holidayDate <= endDate;
  });
}

// Check if a specific date is a holiday
export function isHoliday(date: Date, countryCode: string): Holiday | null {
  const dateStr = date.toISOString().split('T')[0];
  const year = date.getFullYear();
  const holidays = getHolidaysForCountry(countryCode, year);
  return holidays.find(h => h.date === dateStr) || null;
}

// Check if a date is a non-working day (holiday or weekend)
export function isNonWorkingDay(date: Date, countryCode: string, includeWeekends: boolean = true): boolean {
  // Check if it's a weekend
  if (includeWeekends) {
    const dayOfWeek = date.getDay();
    if (dayOfWeek === 0 || dayOfWeek === 6) { // Sunday or Saturday
      return true;
    }
  }
  
  // Check if it's a holiday
  const holiday = isHoliday(date, countryCode);
  return holiday?.isNonWorkingDay || false;
}

// Preload holidays for better performance
export async function preloadHolidays(countryCode: string, years: number[]): Promise<void> {
  const promises = years.map(year => initializeHolidayCache(countryCode, year));
  await Promise.all(promises);
}

// Get country name from code - uses shared constants
export function getCountryName(countryCode: string): string {
  return getCountryNameFromConstants(countryCode);
}

// Check if a country is supported
export function isCountrySupported(countryCode: string): boolean {
  return countryCode in GOOGLE_HOLIDAY_CALENDARS;
}

// Get list of supported countries
export function getSupportedCountries(): Array<{ code: string, name: string }> {
  return Object.keys(GOOGLE_HOLIDAY_CALENDARS).map(code => ({
    code,
    name: getCountryName(code)
  }));
}

// Export the service instance for advanced usage
export { googleCalendarService };